<script setup lang="ts">
import type { ModelProps } from '@/device-based-router/shared'
import { computed } from 'vue'
import { useConnectionType, useInputReport } from '@/composables/useInjectValues'
import { DeviceConnectionType } from '@/device-based-router/shared'
import { DPadDirection } from '@/utils/dualsense/ds.type'
import { inputReportOffsetBluetooth, inputReportOffsetUSB } from '../../_utils/offset.util'

defineProps<ModelProps>()

const inputReport = useInputReport()
const connectionType = useConnectionType()

const offset = computed(() =>
  connectionType.value === DeviceConnectionType.USB ? inputReportOffsetUSB : inputReportOffsetBluetooth,
)
const digitalKeys = computed(() => {
  const keys = inputReport.value.getInt8(offset.value.digitalKeys)
  const keys1 = inputReport.value.getInt8(offset.value.digitalKeys + 1)
  const keys2 = inputReport.value.getInt8(offset.value.digitalKeys + 2)
  return {
    triangle: (keys & 128) !== 0,
    circle: (keys & 64) !== 0,
    cross: (keys & 32) !== 0,
    square: (keys & 16) !== 0,
    option: (keys1 & 32) !== 0,
    create: (keys1 & 16) !== 0,
    r1: (keys1 & 2) !== 0,
    l1: (keys1 & 1) !== 0,
    bR: (keys2 & 128) !== 0,
    bL: (keys2 & 64) !== 0,
    fnR: (keys2 & 32) !== 0,
    fnL: (keys2 & 16) !== 0,
    mic: (keys2 & 4) !== 0,
    touchpad: (keys2 & 2) !== 0,
  }
})

const dpad = computed(() => {
  const direction = inputReport.value.getInt8(offset.value.digitalKeys) & 15
  const up = direction === DPadDirection.U || direction === DPadDirection.UR || direction === DPadDirection.UL
  const right = direction === DPadDirection.R || direction === DPadDirection.UR || direction === DPadDirection.RD
  const down = direction === DPadDirection.D || direction === DPadDirection.RD || direction === DPadDirection.LD
  const left = direction === DPadDirection.L || direction === DPadDirection.UL || direction === DPadDirection.LD
  return { up, right, down, left }
})

const triggerL = computed(() => inputReport.value.getUint8(offset.value.analogTriggerL))
const triggerR = computed(() => inputReport.value.getUint8(offset.value.analogTriggerR))

const triggerLevel = computed(() => {
  const triggerLevel = inputReport.value.getUint8(offset.value.triggerLevel)
  const right = triggerLevel >> 6
  const left = (triggerLevel >> 4) & 3
  return { right, left }
})
</script>

<template>
  <g id="Background" class="ds-fill-primary">
    <g class="ds-fill">
      <path
        id="Active_LTAdapterNode" class="ds-trigger-level" :class="[`level-${triggerLevel.left}`]"
        d="M55.967 338.962c0 5.522-4.494 10.013-10.013 10.013-5.523 0-10.013-4.491-10.013-10.013v-23.627c0-5.519 4.49-10.013 10.013-10.013 5.519 0 10.013 4.494 10.013 10.013v23.627Zm-10.013-35.208c-6.385 0-11.581 5.196-11.581 11.581v23.627c0 6.388 5.196 11.581 11.581 11.581s11.581-5.193 11.581-11.581v-23.627c0-6.385-5.196-11.581-11.581-11.581"
      />
      <path
        id="Active_RTAdapterNode" class="ds-trigger-level" :class="[`level-${triggerLevel.right}`]"
        d="M1276.11 338.962c0 5.522-4.491 10.013-10.014 10.013-5.522 0-10.013-4.491-10.013-10.013v-23.627c0-5.519 4.491-10.013 10.013-10.013 5.523 0 10.014 4.494 10.014 10.013v23.627Zm-10.014-35.208c-6.384 0-11.581 5.196-11.581 11.581v23.627c0 6.388 5.197 11.581 11.581 11.581 6.385 0 11.582-5.193 11.582-11.581v-23.627c0-6.385-5.197-11.581-11.582-11.581"
      />
    </g>
    <path
      v-if="digitalKeys.triangle" id="Active_Triangle"
      d="M1024.99 371.523c21.869 0 39.662-17.792 39.662-39.662 0-21.869-17.793-39.662-39.662-39.662s-39.662 17.793-39.662 39.662c0 21.87 17.793 39.662 39.662 39.662"
    />
    <path
      v-if="digitalKeys.circle" id="Active_Circle"
      d="M1113.19 459.978c21.869 0 39.662-17.792 39.662-39.662 0-21.869-17.793-39.662-39.662-39.662-21.87 0-39.662 17.793-39.662 39.662 0 21.87 17.792 39.662 39.662 39.662"
    />
    <path
      v-if="digitalKeys.square" id="Active_Square"
      d="M938.19 459.338c21.87 0 39.662-17.793 39.662-39.662 0-21.87-17.792-39.662-39.662-39.662-21.869 0-39.662 17.792-39.662 39.662 0 21.869 17.793 39.662 39.662 39.662"
    />
    <path
      v-if="digitalKeys.cross" id="Active_Cross"
      d="M1025.52 546.253c21.87 0 39.662-17.793 39.662-39.662 0-21.87-17.792-39.662-39.662-39.662-21.869 0-39.662 17.792-39.662 39.662 0 21.869 17.793 39.662 39.662 39.662"
    />
    <path
      v-if="digitalKeys.option" id="Active_Option"
      d="M927.216 315.32a13.518 13.518 0 0 0 10.266-1.859 13.528 13.528 0 0 0 5.946-8.571l5.295-24.403c1.594-7.344-3.085-14.616-10.432-16.21-7.336-1.596-14.619 3.086-16.213 10.43l-5.292 24.4c-1.594 7.347 3.085 14.622 10.43 16.213"
    />
    <path
      v-if="digitalKeys.create" id="Active_Create"
      d="M390.32 274.707c-1.594-7.347-8.866-12.032-16.212-10.43-7.345 1.594-12.024 8.866-10.43 16.21l5.295 24.403c1.382 6.374 7.044 10.74 13.32 10.74.953 0 1.92-.099 2.89-.31a13.532 13.532 0 0 0 8.573-5.944 13.542 13.542 0 0 0 1.859-10.269l-5.295-24.4Z"
    />
    <path
      v-if="digitalKeys.bL" id="Active_LBack"
      d="m481.967 859.755-4.126-2.486c-4.882-2.949-10.762-3.168-15.718-.587-10.474 5.454-37.516 21.527-62.266 52.941-23.648 30.018-30.751 55.127-31.936 59.855-.837 3.356-.167 6.929 1.84 9.804l4.925 7.019a7.187 7.187 0 0 0 4.706 1.745 7.18 7.18 0 0 0 2.906-.612c6.707-2.961 19.801-10.604 38.918-29.817a38.535 38.535 0 0 0 15.447 3.22c21.409 0 38.825-17.416 38.825-38.828 0-8.267-2.566-16.132-7.405-22.783 11.283-18.034 15.31-29.766 16.536-34.197.559-2.001-.411-3.922-2.652-5.274"
    />
    <path
      v-if="digitalKeys.bR" id="Active_RBack"
      d="M912.194 910.465c-24.75-31.411-51.789-47.487-62.267-52.94-4.959-2.582-10.838-2.363-15.721.583l-4.122 2.489c-2.242 1.353-3.212 3.273-2.656 5.274 1.226 4.429 5.256 16.163 16.537 34.194-4.836 6.658-7.406 14.52-7.406 22.786 0 21.409 17.42 38.829 38.829 38.829a38.537 38.537 0 0 0 15.446-3.224c19.115 19.217 32.211 26.856 38.918 29.821a7.067 7.067 0 0 0 2.86.605c1.729 0 3.461-.633 4.879-1.881l4.799-6.877c2.007-2.878 2.677-6.45 1.837-9.804-1.183-4.728-8.285-29.836-31.933-59.855"
    />
    <path
      v-if="digitalKeys.mic" id="Active_Mute"
      d="M685.203 645.017a6.324 6.324 0 0 0-6.318-6.316h-44.371a6.324 6.324 0 0 0-6.315 6.316 6.323 6.323 0 0 0 6.315 6.315h44.371a6.323 6.323 0 0 0 6.318-6.315"
    />
    <path
      id="Active_RT" :opacity="triggerR / 255"
      d="M962.462 156.389c-.08-27.087 9.597-58.505 23.931-82.314 9.629-15.994 21.375-28.547 33.64-34.163 28.179-12.904 52.648 8.163 63.009 24.966 13.032 21.134 20.647 64.627 17.077 110.094-.057.733-.373 1.409-.975 2.008-.661.658-1.736 1.243-3.211 1.693-4.863 1.486-14.618 1.728-26.75 1.039-31.948-1.816-80.576-9.933-98.763-17.098-2.88-1.135-5.027-2.267-6.292-3.331-1.16-.975-1.663-1.988-1.666-2.894Z"
    />
    <path
      id="Active_LT" :opacity="triggerL / 255"
      d="M356.862 156.389c-.003.906-.507 1.919-1.666 2.894-1.266 1.064-3.412 2.196-6.292 3.331-18.187 7.165-66.815 15.282-98.763 17.098-12.132.689-21.887.447-26.751-1.039-1.474-.45-2.549-1.035-3.21-1.693-.602-.599-.918-1.275-.975-2.008-3.57-45.467 4.045-88.96 17.077-110.094 10.361-16.803 34.829-37.87 63.009-24.966 12.265 5.616 24.01 18.169 33.64 34.163 14.334 23.809 24.011 55.227 23.931 82.314Z"
    />
    <path
      v-if="digitalKeys.r1" id="Active_R1"
      d="m947.584 219.071-2.642-.453 2.893-20.305a5.926 5.926 0 0 1 4.5-4.963c5.975-1.433 22.153-4.765 43.291-4.765 12.661 0 25.115 1.194 37.013 3.549 35.963 7.119 60.985 22.699 70.075 29.066a11.03 11.03 0 0 1 4.596 7.628l2.291 18.042-2.762-.474.163 1.292c-10.372-2.188-25.631-5.271-47.408-9.398l-79.271-13.602c-3.971-.567-7.731-1.092-11.283-1.588-5.901-.822-11.225-1.564-15.988-2.29-1.904-.289-3.782-.415-5.648-.471l.18-1.268Z"
    />
    <path
      v-if="digitalKeys.l1" id="Active_L1"
      d="m205.207 246.396-2.76.474 2.291-18.042a11.023 11.023 0 0 1 4.596-7.628c9.09-6.367 34.112-21.947 70.072-29.066 11.903-2.355 24.355-3.549 37.013-3.549 21.144 0 37.319 3.332 43.294 4.765a5.927 5.927 0 0 1 4.497 4.963l2.896 20.305-2.642.453.18 1.268c-1.866.053-3.747.182-5.648.471-4.763.726-10.087 1.468-15.988 2.29-3.551.496-7.311 1.021-11.282 1.588l-79.254 13.599c-21.787 4.129-37.052 7.214-47.429 9.401l.164-1.292Z"
    />
    <path
      v-if="digitalKeys.fnL" id="Active_LFn"
      d="M498.58 748.209a16.214 16.214 0 0 0 3.332-6.028l9.054-30.497c.243-.825.08-1.69-.448-2.368a2.554 2.554 0 0 0-2.164-.996c-11.999.52-30.242.943-40.666.943-14.493 0-33.874-1.197-41.532-1.712a2.517 2.517 0 0 0-2.164.946 2.614 2.614 0 0 0-.466 2.41l9.16 31.276a16.219 16.219 0 0 0 3.323 6.018 17.224 17.224 0 0 0 10.26 5.189c6.059.788 13.778 1.546 21.031 1.546 7.253 0 14.97-.758 21.028-1.546a17.22 17.22 0 0 0 10.252-5.181Z"
    />
    <path
      v-if="digitalKeys.fnR" id="Active_RFn"
      d="M876.023 748.217a16.208 16.208 0 0 0 3.337-6.036l9.163-31.274a2.626 2.626 0 0 0-.469-2.413 2.499 2.499 0 0 0-2.164-.943c-7.657.515-27.038 1.712-41.531 1.712-10.422 0-28.667-.423-40.667-.943-.04-.003-.08-.003-.117-.003a2.56 2.56 0 0 0-2.044.999 2.596 2.596 0 0 0-.447 2.368l9.053 30.497a16.214 16.214 0 0 0 3.333 6.031 17.222 17.222 0 0 0 10.25 5.178c6.058.788 13.775 1.546 21.028 1.546 7.253 0 14.97-.758 21.028-1.546a17.222 17.222 0 0 0 10.247-5.173Z"
    />
    <path
      v-if="digitalKeys.touchpad" id="Touchpad"
      d="M893.508 241.197c-.007.11-.022.219-.045.328v.003l-36.199 175.533c-5.737 27.81-30.51 47.994-58.904 47.994H514.041c-28.396 0-53.169-20.184-58.904-47.994l-6.449-31.268v-.008l-29.741-144.198-.011-.091a2.328 2.328 0 0 1-.038-.299h-.005s-.244-2.727-.262-2.709c-.158-1.755-.322-3.897-.325-5.435-.016-5.255 2.341-11.544 20.841-13.585 61.095-6.736 134.063-10.153 216.877-10.153 83.047 0 156.133 3.417 217.23 10.153 17.954 1.98 20.694 7.961 20.828 13.114l-.006.651h.014c-.014 1.526-.166 3.57-.319 5.258-.029-.029-.259 2.706-.259 2.706h-.004Z"
    />
    <path
      v-if="dpad.up" id="ActiveDPad"
      d="M256.174 372.092c.447 4.031 1.909 6.964 5.22 10.47l17.144 18.151.014.014c2.552 2.552 5.935 4.012 9.291 4.012h.097c3.452-.029 6.744-1.559 9.524-4.43l17.53-18.744c2.748-2.745 4.262-5.78 4.634-9.299l2.017-28.841c.257-3.804-.099-13.224-8.997-18.57-4.813-2.895-12.157-4.13-24.548-4.13-15.229 0-25.121 2.644-30.234 8.084-3.286 3.495-4.7 8.209-4.317 14.428l2.625 28.855Z"
    />
    <path
      v-if="dpad.down" id="ActiveDPad1"
      d="M319.536 466.448c-.447-4.031-1.91-6.964-5.22-10.47l-17.145-18.152-.013-.013c-2.553-2.553-5.936-4.012-9.292-4.012h-.096c-3.453.029-6.744 1.559-9.525 4.43l-17.53 18.743c-2.748 2.746-4.261 5.78-4.634 9.3l-2.016 28.841c-.258 3.803.099 13.223 8.996 18.569 4.814 2.896 12.158 4.131 24.548 4.131 15.23 0 25.121-2.644 30.234-8.084 3.286-3.495 4.701-8.209 4.318-14.429l-2.625-28.854Z"
    />
    <path
      v-if="dpad.right" id="ActiveDPad2"
      d="M335.432 387.939c-4.031.448-6.964 1.91-10.47 5.221l-18.151 17.144-.014.014c-2.552 2.552-4.012 5.935-4.012 9.291v.096c.029 3.453 1.559 6.745 4.43 9.525l18.744 17.53c2.745 2.748 5.78 4.261 9.299 4.634l28.841 2.017c3.804.257 13.224-.1 18.57-8.997 2.895-4.813 4.13-12.158 4.13-24.548 0-15.229-2.644-25.121-8.084-30.234-3.495-3.286-8.209-4.7-14.428-4.317l-28.855 2.624Z"
    />
    <path
      v-if="dpad.left" id="ActiveDPad3"
      d="M240.605 450.539c4.031-.447 6.964-1.91 10.47-5.22l18.151-17.145.014-.013c2.552-2.553 4.012-5.936 4.012-9.292v-.096c-.029-3.453-1.559-6.744-4.43-9.525l-18.744-17.53c-2.745-2.748-5.78-4.261-9.299-4.634l-28.841-2.016c-3.804-.257-13.224.099-18.57 8.996-2.895 4.814-4.13 12.158-4.13 24.548 0 15.23 2.644 25.121 8.084 30.234 3.495 3.287 8.209 4.701 14.428 4.318l28.855-2.625Z"
    />
  </g>
</template>

<style scoped lang="scss">
.ds-fill-primary {
  @apply fill-primary;
}

.ds-fill {
  @apply fill-black dark-fill-white;
}

.ds-trigger-level {
  @apply transform-gpu transition-transform;

  &.level-0 {
    @apply translate-y--30px;
  }

  &.level-2 {
    @apply translate-y-30px;
  }
}
</style>
